import { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { AddPhotoAlternate, RemoveCircle } from "@mui/icons-material";
import { useFormik } from "formik";
import Yup from "validation/Validation";
import { Pane, Spinner } from "evergreen-ui";
import { useParams } from "react-router-dom";
import getSlug from "speakingurl";
import ImageUploading from "react-images-uploading";
import {
  useAddCategoryMutation,
  useGetCategoriesQuery,
  useSingleCategoryMutation,
  useUpdateCategoryMutation,
} from "redux/api/slices/server";
import FormBottom from "./FormBottom";
import DetailTitle from "./DetailTitle";
import DetailAlerts from "./DetailAlerts";
import CategoryForm from "./CategoryForm";

const DetailCategoryPage = () => {
  const { id } = useParams();
  const [content, setContent] = useState("");

  const initVal = {
    title: "",
    alttag: "",
    page_title: "",
    page_description: "",
    page_keywords: "",
  };

  const [initialValues, setInitialValues] = useState(initVal);
  const [images, setImages] = useState([]);

  const onChangeImages = (imageList, addUpdateIndex) => {
    console.log(imageList);
    setImages(imageList);
  };

  const categories = useGetCategoriesQuery();
  const [addCategory, insertData] = useAddCategoryMutation();
  const [singleCategory, singleData] = useSingleCategoryMutation();
  const [updateCategory, updateData] = useUpdateCategoryMutation();
  const [ustKategori, setUstKategori] = useState(0);

  const handleChangeEditor = (value) => {
    setContent(value);
  };

  const formFields = {
    title: Yup.string().required(),
    page_title: Yup.string(),
    page_description: Yup.string(),
    parantId: Yup.number(),
  };

  const validationSchema = Yup.object(formFields);

  const { handleSubmit, handleChange, values, errors } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      const categoryObject = {
        title: values.title,
        content: content,
        parantId: ustKategori,
        page_title: values.page_title,
        page_description: values.page_description,
        page_keywords: values.page_keywords,
        image: images,
        slug: getSlug(values.title),
      };

      if (id > 0) {
        updateCategory(Object.assign(categoryObject, { id: parseInt(id) }));
        setInitialValues(categoryObject);
      } else {
        setInitialValues(initVal);
        setContent("");
        setUstKategori(0);
        addCategory(categoryObject);
      }
    },
  });

  useEffect(() => {
    singleCategory({ id });
  }, [id, singleCategory]);

  useEffect(() => {
    if (id > 0) {
      if (singleData.isSuccess) {
        setUstKategori(singleData.data.category.ust_id);
        setContent(singleData.data.category.tr_aciklama);

        if (singleData.data.uploadedImage.Base64 !== null) {
          setImages([{ data_url: singleData.data.uploadedImage.Base64 }]);
        }

        setInitialValues({
          title: singleData.data.category.tr_baslik,
          page_title: singleData.data.category.baslik,
          page_description: singleData.data.category.aciklama,
        });
      }
    }
  }, [id, singleData]);

  if (insertData.isLoading || singleData.isLoading || updateData.isLoading) {
    return (
      <div className="w-full flex justify-center items-center">
        <Pane>
          <Spinner marginX="auto" size={60} marginTop={25} marginBottom={50} />
          <Typography variant="h5" gutterBottom>
            İşlem Yapılıyor. Lütfen Bekleyniz...
          </Typography>
        </Pane>
      </div>
    );
  }

  return (
    <div className="w-full my-6">
      <div className="w-full h-auto container mx-auto">
        <DetailTitle id={id} />
        <div className="w-full h-auto flex flex-col p-5 gap-4 border shadow bg-white">
          <DetailAlerts
            id={id}
            insertData={insertData}
            initialValues={initialValues}
            updateData={updateData}
          />
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 1 }}
          >
            <CategoryForm
              initialValues={initialValues}
              handleChange={handleChange}
              content={content}
              handleChangeEditor={handleChangeEditor}
              categories={categories}
              ustKategori={ustKategori}
              setUstKategori={setUstKategori}
              errors={errors}
              values={values}
            />
            <div className="w-full">
              <ImageUploading
                multiple={false}
                value={images}
                onChange={onChangeImages}
                dataURLKey="data_url"
                acceptType={["jpg", "png", "webp", "jpeg", "webp"]}
              >
                {({
                  imageList,
                  onImageUpload,
                  onImageRemoveAll,
                  onImageUpdate,
                  onImageRemove,
                  isDragging,
                  dragProps,
                }) => (
                  <div className="w-full block">
                    <div className="w-full flex items-center gap-4">
                      <Button
                        size="small"
                        startIcon={<AddPhotoAlternate />}
                        variant="outlined"
                        sx={{ color: isDragging ? "red" : null }}
                        onClick={onImageUpload}
                        {...dragProps}
                        lang="tr"
                      >
                        Kategori Görseli Yükle
                      </Button>
                      {images.length > 0 && (
                        <Button
                          size="small"
                          startIcon={<RemoveCircle />}
                          variant="outlined"
                          onClick={onImageRemoveAll}
                          lang="tr"
                        >
                          Tüm Görselleri Kaldır
                        </Button>
                      )}
                    </div>
                    <div
                      className="w-full flex items-center justify-center p-4 my-2 min-h-[140px]
                      rounded-lg border-dashed border-2 border-indigo-400 bg-gray-200 bg-opacity-60"
                    >
                      {imageList.map((image, index) => (
                        <div
                          key={index}
                          className="w-2/3 mx-auto flex flex-col gap-1 bg-white shadow"
                        >
                          <img src={image.data_url} alt="" />
                          <div className="w-full flex justify-between text-sm py-1 px-3">
                            <button
                              className="block px-4 py-1 rounded bg-[#3460b1] text-white"
                              type="button"
                              onClick={() => onImageUpdate(index)}
                            >
                              Değiştir
                            </button>
                            <button
                              className="block px-4 py-1 rounded bg-[#b33232] text-white"
                              type="button"
                              onClick={() => onImageRemove(index)}
                            >
                              Kaldır
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ImageUploading>
            </div>
            <FormBottom />
          </Box>
        </div>
      </div>
    </div>
  );
};

export default DetailCategoryPage;
